/**
 * @zendir/ui - DataTable Component
 *
 * Structured data display with expandable groups, responsive columns,
 * and theme-aware styling. Designed for telemetry parameter grids,
 * link budget displays, spacecraft information panels.
 *
 * Astro UX Compliance:
 * - Tabular monospace numbers for value columns
 * - Status-based row coloring
 * - Compact mode support
 * - Accessible table semantics
 *
 * Enhanced Tooltip System:
 * - Row-level tooltips (string or rich ReactNode via rowTooltipContent)
 * - Cell-level tooltips per column definition
 * - Header tooltips for column descriptions
 * - Built-in DataTableRowDetail component for structured detail views
 * - Portal-rendered, WCAG-compliant, animated tooltips
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={[
 *     { key: 'label', header: 'Parameter', width: '40%', headerTooltip: 'Telemetry parameter name' },
 *     { key: 'value', header: 'Value', align: 'right', mono: true, cellTooltip: (v, row) => `Raw: ${v}` },
 *     { key: 'unit', header: 'Unit', width: 80 },
 *   ]}
 *   data={[
 *     { label: 'Frequency', value: '437.500', unit: 'MHz' },
 *     { label: 'Bandwidth', value: '25.000', unit: 'kHz' },
 *   ]}
 *   rowTooltipContent={(row) => (
 *     <DataTableRowDetail
 *       title={row.label}
 *       fields={[
 *         { label: 'Value', value: `${row.value} ${row.unit}` },
 *       ]}
 *     />
 *   )}
 * />
 * ```
 */

import React, { memo, useState, useCallback, useMemo } from "react";
import { useTheme } from "../../theme";
import { useBreakpoint } from "../layout/useBreakpoint";
import { Tooltip } from "../overlays/Tooltip";
import type { TooltipPlacement } from "../overlays/Tooltip";
import { addAlpha } from "../../utils";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DataTableColumn<T = Record<string, unknown>> {
  /** Key in data object */
  key: string;
  /** Column header text */
  header: string;
  /** Column width (CSS value or number for px) */
  width?: string | number;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Use monospace font (good for numbers) */
  mono?: boolean;
  /** Custom cell renderer */
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
  /** Hide column on mobile */
  hideOnMobile?: boolean;
  /** Tooltip for the column header (explains what this column represents) */
  headerTooltip?: string;
  /** Per-cell tooltip — return string or ReactNode, or undefined to skip */
  cellTooltip?: (
    value: unknown,
    row: T,
    index: number,
  ) => React.ReactNode | undefined;
}

export interface DataTableProps<T = Record<string, unknown>> {
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Data rows */
  data: T[];
  /** Key field for React keys (default: index) */
  keyField?: string;
  /** Group rows by this field */
  groupBy?: string;
  /** Allow expanding/collapsing groups */
  expandable?: boolean;
  /** Default expanded state for groups */
  defaultExpanded?: boolean;
  /** Striped rows */
  striped?: boolean;
  /** Compact row padding */
  compact?: boolean;
  /** Show header row */
  showHeader?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Row click handler */
  onRowClick?: (row: T, index: number) => void;
  /** Simple string tooltip for each row (legacy — uses native title) */
  rowTooltip?: (row: T) => string | undefined;
  /** Rich tooltip content for each row — renders in a portal-based Tooltip */
  rowTooltipContent?: (row: T, index: number) => React.ReactNode | undefined;
  /** Placement for row and cell tooltips (default: 'top') */
  tooltipPlacement?: TooltipPlacement;
  /** Delay before tooltips appear in ms (default: 300) */
  tooltipDelay?: number;
  /** Max height with scroll */
  maxHeight?: string | number;
  /** Color for boolean ✓/✗ values (e.g. '#fff' for white). Defaults to text.primary or row status. */
  booleanColor?: string;
  /** Optional row status key or getter — boolean cells use this row's status color when set (matches white/status design). */
  getRowStatus?: (
    row: T,
    index: number,
  ) =>
    | "normal"
    | "caution"
    | "serious"
    | "critical"
    | "standby"
    | "off"
    | undefined;
  /** Custom style */
  style?: React.CSSProperties;
}

// ─── DataTableRowDetail ──────────────────────────────────────────────────────

export interface DataTableRowDetailField {
  /** Field label */
  label: string;
  /** Field value (string, number, or ReactNode for custom rendering) */
  value: React.ReactNode;
  /** Optional status color for the value */
  status?: "normal" | "caution" | "serious" | "critical" | "standby" | "off";
  /** Use monospace font for this value */
  mono?: boolean;
}

export interface DataTableRowDetailProps {
  /** Title shown at the top of the tooltip */
  title?: React.ReactNode;
  /** Optional subtitle / secondary info */
  subtitle?: React.ReactNode;
  /** Structured key-value fields to display */
  fields?: DataTableRowDetailField[];
  /** Optional footer content (timestamps, actions, etc.) */
  footer?: React.ReactNode;
  /** Optional icon or status indicator beside the title */
  icon?: React.ReactNode;
  /** Maximum width of the tooltip (default: 320) */
  maxWidth?: number;
}

/**
 * Pre-built tooltip content renderer for DataTable rows.
 * Provides a structured layout: title, key-value fields, and optional footer.
 *
 * @example
 * ```tsx
 * <DataTable
 *   columns={columns}
 *   data={data}
 *   rowTooltipContent={(row) => (
 *     <DataTableRowDetail
 *       title={row.parameter}
 *       subtitle={row.category}
 *       fields={[
 *         { label: 'Value', value: row.value, mono: true },
 *         { label: 'Status', value: row.status, status: row.status },
 *       ]}
 *       footer={`Last updated: ${row.timestamp}`}
 *     />
 *   )}
 * />
 * ```
 */
export const DataTableRowDetail = memo(function DataTableRowDetail({
  title,
  subtitle,
  fields,
  footer,
  icon,
  maxWidth = 320,
}: DataTableRowDetailProps): React.ReactElement {
  const { tokens } = useTheme();

  return (
    <div style={{ maxWidth, minWidth: 180 }}>
      {/* Header */}
      {(title || icon) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
            marginBottom: fields?.length || subtitle ? tokens.spacing.sm : 0,
          }}
        >
          {icon}
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div
                style={{
                  fontWeight: tokens.typography.fontWeight.semibold,
                  fontSize: tokens.typography.fontSize.sm,
                  color: tokens.colors.text.primary,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </div>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: tokens.typography.fontSize.xxs,
                  color: tokens.colors.text.tertiary,
                  marginTop: 2,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fields */}
      {fields && fields.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: `${tokens.spacing.xs} ${tokens.spacing.md}`,
            fontSize: tokens.typography.fontSize.xs,
            lineHeight: 1.5,
          }}
        >
          {fields.map((field, i) => (
            <React.Fragment key={i}>
              <span
                style={{
                  color: tokens.colors.text.tertiary,
                  whiteSpace: "nowrap",
                }}
              >
                {field.label}
              </span>
              <span
                style={{
                  color: field.status
                    ? tokens.colors.status[field.status] ||
                      tokens.colors.text.primary
                    : tokens.colors.text.primary,
                  fontFamily: field.mono
                    ? tokens.typography.fontFamily.mono
                    : "inherit",
                  fontVariantNumeric: field.mono ? "tabular-nums" : "normal",
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {field.value}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div
          style={{
            marginTop: tokens.spacing.sm,
            paddingTop: tokens.spacing.xs,
            borderTop: `1px solid ${addAlpha(tokens.colors.border.muted, 0.19)}`,
            fontSize: tokens.typography.fontSize.xxs,
            color: tokens.colors.text.tertiary,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
});

// ─── Cell Tooltip Wrapper ────────────────────────────────────────────────────

interface CellTooltipWrapperProps {
  content: React.ReactNode;
  placement: TooltipPlacement;
  delay: number;
  children: React.ReactElement;
}

const CellTooltipWrapper = memo(function CellTooltipWrapper({
  content,
  placement,
  delay,
  children,
}: CellTooltipWrapperProps): React.ReactElement {
  if (!content) return children;
  return (
    <Tooltip content={content} placement={placement} delay={delay}>
      {children}
    </Tooltip>
  );
});

// ─── DataTable ───────────────────────────────────────────────────────────────

export const DataTable = memo(function DataTable<
  T extends Record<string, unknown>,
>({
  columns,
  data,
  keyField,
  groupBy,
  expandable = false,
  defaultExpanded = true,
  striped = false,
  compact = false,
  showHeader = true,
  emptyMessage = "No data available",
  onRowClick,
  rowTooltip,
  rowTooltipContent,
  tooltipPlacement = "top",
  tooltipDelay = 300,
  maxHeight,
  booleanColor,
  getRowStatus,
  style,
}: DataTableProps<T>): React.ReactElement {
  const { tokens } = useTheme();
  const { isMobile: isMobileView } = useBreakpoint();
  const statusColorMap = useMemo(
    () => ({
      normal: tokens.colors.status.normal,
      caution: tokens.colors.status.caution,
      serious: tokens.colors.status.serious,
      critical: tokens.colors.status.critical,
      standby: tokens.colors.status.standby,
      off: tokens.colors.status.off,
    }),
    [tokens.colors.status],
  );
  const visibleColumns = isMobileView
    ? columns.filter((col) => !col.hideOnMobile)
    : columns;

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    defaultExpanded ? new Set(["__all__"]) : new Set(),
  );
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const visibleColumnCount = Math.max(1, visibleColumns.length);

  const cellPadding = compact
    ? `${tokens.spacing.xs} ${tokens.spacing.sm}`
    : `${tokens.spacing.sm} ${tokens.spacing.md}`;

  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  const isGroupExpanded = useCallback(
    (group: string) => {
      return expandedGroups.has("__all__") || expandedGroups.has(group);
    },
    [expandedGroups],
  );

  const groups = useMemo(() => {
    if (!groupBy) return [{ key: "__default__", label: "", rows: data }];

    const map = new Map<string, T[]>();
    data.forEach((row) => {
      const key = String((row as Record<string, unknown>)[groupBy] ?? "Other");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    });

    return Array.from(map.entries()).map(([key, rows]) => ({
      key,
      label: key,
      rows,
    }));
  }, [data, groupBy]);

  const resolveWidth = (w: string | number | undefined): string | undefined => {
    if (w === undefined) return undefined;
    if (typeof w === "number") return `${w}px`;
    return w;
  };

  const renderCell = (col: DataTableColumn<T>, row: T, rowIndex: number) => {
    const value = row[col.key];
    if (col.render) return col.render(value, row, rowIndex);
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") {
      const label = value ? "✓" : "✗";
      const color =
        booleanColor ??
        (getRowStatus
          ? (() => {
              const s = getRowStatus(row, rowIndex);
              return s ? statusColorMap[s] : undefined;
            })()
          : undefined) ??
        tokens.colors.text.primary;
      return <span style={{ color }}>{label}</span>;
    }
    return String(value);
  };

  const renderCellWithTooltip = (
    col: DataTableColumn<T>,
    row: T,
    rowIndex: number,
  ) => {
    const cellContent = renderCell(col, row, rowIndex);
    if (!col.cellTooltip) return cellContent;

    const tooltipContent = col.cellTooltip(row[col.key], row, rowIndex);
    if (!tooltipContent) return cellContent;

    return (
      <CellTooltipWrapper
        content={tooltipContent}
        placement={tooltipPlacement}
        delay={tooltipDelay}
      >
        <span style={{ cursor: "help" }}>{cellContent}</span>
      </CellTooltipWrapper>
    );
  };

  const renderHeaderCell = (col: DataTableColumn<T>) => {
    const headerContent = (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {col.header}
        {col.headerTooltip && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            style={{ opacity: 0.5, flexShrink: 0 }}
          >
            <circle
              cx="8"
              cy="8"
              r="7"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M8 7v4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle cx="8" cy="5" r="0.75" fill="currentColor" />
          </svg>
        )}
      </span>
    );

    if (!col.headerTooltip) return headerContent;

    return (
      <Tooltip
        content={col.headerTooltip}
        placement="bottom"
        delay={tooltipDelay}
      >
        {headerContent}
      </Tooltip>
    );
  };

  const chevron = (expanded: boolean) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      style={{
        transition: tokens.animation.fast,
        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
        flexShrink: 0,
      }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );

  const hasRichRowTooltip = !!rowTooltipContent;

  const renderRowContent = (row: T, rowIdx: number, localIndex: number) => {
    const isHovered = hoveredRow === rowIdx;
    const rowKey = keyField ? String(row[keyField]) : rowIdx;
    const useNativeTitle = !hasRichRowTooltip && rowTooltip;

    const tr = (
      <tr
        key={rowKey}
        tabIndex={onRowClick ? 0 : undefined}
        onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
        onKeyDown={
          onRowClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(row, rowIdx);
                }
              }
            : undefined
        }
        onMouseEnter={() => setHoveredRow(rowIdx)}
        onMouseLeave={() => setHoveredRow(null)}
        title={useNativeTitle ? rowTooltip(row) : undefined}
        style={{
          cursor: onRowClick ? "pointer" : "default",
          backgroundColor: isHovered
            ? addAlpha(tokens.colors.accent.primary, 0.08)
            : striped && localIndex % 2 === 1
              ? addAlpha(tokens.colors.background.surface, 0.5)
              : "transparent",
          transition: "background-color 150ms ease",
        }}
      >
        {visibleColumns.map((col) => (
          <td
            key={col.key}
            style={{
              padding: cellPadding,
              textAlign: col.align || "left",
              color: tokens.colors.text.primary,
              borderBottom: `1px solid ${addAlpha(tokens.colors.border.muted, 0.08)}`,
              fontFamily: col.mono
                ? tokens.typography.fontFamily.mono
                : "inherit",
              fontVariantNumeric: col.mono ? "tabular-nums" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: resolveWidth(col.width) || "300px",
            }}
          >
            {renderCellWithTooltip(col, row, rowIdx)}
          </td>
        ))}
      </tr>
    );

    if (hasRichRowTooltip) {
      const tooltipContent = rowTooltipContent(row, rowIdx);
      if (tooltipContent) {
        return (
          <Tooltip
            key={rowKey}
            content={tooltipContent}
            placement={tooltipPlacement}
            delay={tooltipDelay}
          >
            {tr}
          </Tooltip>
        );
      }
    }

    return tr;
  };

  let globalRowIndex = 0;

  return (
    <div
      style={{
        fontFamily: tokens.typography.fontFamily.primary,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
        borderRadius: tokens.borderRadius.md,
        border: tokens.borders.divider,
        ...style,
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: compact
            ? tokens.typography.fontSize.xs
            : tokens.typography.fontSize.sm,
        }}
      >
        {showHeader && (
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: cellPadding,
                    textAlign: col.align || "left",
                    fontSize: tokens.typography.fontSize.xxs,
                    fontWeight: tokens.typography.fontWeight.bold,
                    color: tokens.colors.text.tertiary,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    backgroundColor: tokens.colors.background.surface,
                    borderBottom: tokens.borders.divider,
                    width: resolveWidth(col.width),
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {renderHeaderCell(col)}
                </th>
              ))}
            </tr>
          </thead>
        )}

        <tbody>
          {data.length === 0 && (
            <tr>
              <td
                colSpan={visibleColumnCount}
                style={{
                  padding: tokens.spacing.xl,
                  textAlign: "center",
                  color: tokens.colors.text.tertiary,
                  fontSize: tokens.typography.fontSize.sm,
                }}
              >
                {emptyMessage}
              </td>
            </tr>
          )}

          {groups.map((group) => {
            const isExpanded = !groupBy || isGroupExpanded(group.key);

            return (
              <React.Fragment key={group.key}>
                {groupBy && group.label && (
                  <tr
                    style={{
                      backgroundColor: addAlpha(
                        tokens.colors.accent.primary,
                        0.08,
                      ),
                    }}
                  >
                    <td
                      colSpan={visibleColumnCount}
                      style={{
                        padding: 0,
                        borderBottom: tokens.borders.divider,
                      }}
                    >
                      {expandable ? (
                        <button
                          type="button"
                          aria-expanded={isExpanded}
                          onClick={() => toggleGroup(group.key)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: tokens.spacing.xs,
                            width: "100%",
                            padding: cellPadding,
                            margin: 0,
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            fontWeight: tokens.typography.fontWeight.semibold,
                            color: tokens.colors.text.primary,
                            fontSize: compact
                              ? tokens.typography.fontSize.xs
                              : tokens.typography.fontSize.sm,
                            fontFamily: "inherit",
                            textAlign: "left",
                          }}
                        >
                          {chevron(isExpanded)}
                          {group.label}
                          <span
                            style={{
                              fontSize: tokens.typography.fontSize.xxs,
                              color: tokens.colors.text.tertiary,
                              fontWeight: tokens.typography.fontWeight.normal,
                            }}
                          >
                            ({group.rows.length})
                          </span>
                        </button>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: tokens.spacing.xs,
                            padding: cellPadding,
                            fontWeight: tokens.typography.fontWeight.semibold,
                            color: tokens.colors.text.primary,
                            fontSize: compact
                              ? tokens.typography.fontSize.xs
                              : tokens.typography.fontSize.sm,
                          }}
                        >
                          {group.label}
                          <span
                            style={{
                              fontSize: tokens.typography.fontSize.xxs,
                              color: tokens.colors.text.tertiary,
                              fontWeight: tokens.typography.fontWeight.normal,
                            }}
                          >
                            ({group.rows.length})
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                )}

                {isExpanded &&
                  group.rows.map((row, localIndex) => {
                    const rowIdx = globalRowIndex++;
                    return renderRowContent(row, rowIdx, localIndex);
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}) as <T extends Record<string, unknown>>(
  props: DataTableProps<T>,
) => React.ReactElement;

export default DataTable;
